// src/AudioSpectrum/AudioFileUploader.js

import React, { useRef, useState } from 'react';
import { Upload, Music, File } from 'lucide-react';
import './AudioFileUploader.css';

const AudioFileUploader = ({ onFileLoad, isLoading, error, language }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const supportedFormats = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/m4a', 'video/mp4'];
  
  const handleFileSelect = (file) => {
    if (!file) return;
    
    // 파일 형식 검증
    if (!supportedFormats.some(format => file.type.includes(format.split('/')[1]))) {
      alert(language === 'KR' ? 
        '지원되지 않는 파일 형식입니다. MP3, WAV, OGG, MP4, M4A 파일을 사용해주세요.' :
        'Unsupported file format. Please use MP3, WAV, OGG, MP4, or M4A files.'
      );
      return;
    }
    
    // 파일 크기 검증 (100MB 제한)
    if (file.size > 100 * 1024 * 1024) {
      alert(language === 'KR' ? 
        '파일 크기가 너무 큽니다. 100MB 이하의 파일을 사용해주세요.' :
        'File size too large. Please use files under 100MB.'
      );
      return;
    }
    
    onFileLoad(file);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };
  
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };
  
  const handleClick = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <div className="audio-file-uploader">
      <div 
        className={`upload-area ${isDragOver ? 'drag-over' : ''} ${isLoading ? 'loading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,video/mp4"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        
        {isLoading ? (
          <div className="loading-content">
            <div className="spinner"></div>
            <p>{language === 'KR' ? '파일을 처리하는 중...' : 'Processing file...'}</p>
          </div>
        ) : (
          <div className="upload-content">
            <Upload size={48} className="upload-icon" />
            <h3>
              {language === 'KR' ? 
                '오디오 파일을 선택하거나 드래그하세요' : 
                'Select or drag an audio file'
              }
            </h3>
            <p>
              {language === 'KR' ? 
                'MP3, WAV, OGG, MP4, M4A 형식 지원 (최대 100MB)' :
                'Supports MP3, WAV, OGG, MP4, M4A formats (max 100MB)'
              }
            </p>
            <button className="select-button">
              <File size={20} />
              {language === 'KR' ? '파일 선택' : 'Select File'}
            </button>
          </div>
        )}
      </div>
      
      <div className="supported-formats">
        <h4>{language === 'KR' ? '지원되는 형식:' : 'Supported formats:'}</h4>
        <div className="format-list">
          <span className="format-tag">MP3</span>
          <span className="format-tag">WAV</span>
          <span className="format-tag">OGG</span>
          <span className="format-tag">MP4</span>
          <span className="format-tag">M4A</span>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default AudioFileUploader;